import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend } from "recharts";
import { Fire, Trophy, CalendarCheck, Percent } from "@phosphor-icons/react";
import DayDetailSheet from "./DayDetailSheet";
import FitnessTrendCard from "./FitnessTrendCard";
import YearCompareCard from "./YearCompareCard";
import MonthReportCard from "./MonthReportCard";
import BestDaysCard from "./BestDaysCard";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const recColor = (v) => (v == null ? "hsl(220 10% 16%)" : v >= 67 ? "#00e676" : v >= 34 ? "#ffea00" : "#ff3b30");
const zoneLabel = (v) => (v == null ? "нет данных" : v >= 67 ? "зелёная" : v >= 34 ? "жёлтая" : "красная");

function computeStreaks(days) {
  let current = 0, record = 0, run = 0, green = 0, withData = 0;
  days.forEach((d) => {
    if (d.recovery == null) return;
    withData++;
    if (d.recovery >= 67) {
      green++;
      run++;
      record = Math.max(record, run);
    } else run = 0;
  });
  for (let i = days.length - 1; i >= 0; i--) {
    const r = days[i].recovery;
    if (r == null) continue;
    if (r >= 67) current++;
    else break;
  }
  return { current, record, green, withData, pct: withData ? Math.round((green / withData) * 100) : 0 };
}

function weekdayStats(days) {
  const acc = WEEKDAYS.map((w) => ({ day: w, rec: [], sleep: [], strain: [] }));
  days.forEach((d) => {
    const idx = (new Date(d.date + "T12:00:00").getDay() + 6) % 7;
    if (d.recovery != null) acc[idx].rec.push(d.recovery);
    if (d.sleep_h != null) acc[idx].sleep.push(d.sleep_h);
    if (d.strain != null) acc[idx].strain.push(d.strain);
  });
  const mean = (a) => (a.length ? Math.round((a.reduce((x, y) => x + y, 0) / a.length) * 10) / 10 : null);
  return acc.map((a) => ({ day: a.day, recovery: mean(a.rec), sleep_h: mean(a.sleep), strain: mean(a.strain) }));
}

function Heatmap({ days, onDayClick }) {
  const map = Object.fromEntries(days.map((d) => [d.date, d]));
  const today = new Date();
  const cells = [];
  for (let i = 364; i >= 0; i--) {
    const dt = new Date(today);
    dt.setDate(today.getDate() - i);
    cells.push(dt.toISOString().slice(0, 10));
  }
  const offset = (new Date(cells[0] + "T12:00:00").getDay() + 6) % 7;
  const padded = [...Array(offset).fill(null), ...cells];
  const weeks = [];
  for (let i = 0; i < padded.length; i += 7) weeks.push(padded.slice(i, i + 7));
  let lastMonth = null;
  const weekLabels = weeks.map((week) => {
    const first = week.find(Boolean);
    if (!first) return "";
    const m = parseInt(first.slice(5, 7), 10) - 1;
    if (m !== lastMonth) { lastMonth = m; return MONTHS[m]; }
    return "";
  });
  return (
    <div className="overflow-x-auto pb-2" data-testid="recovery-heatmap">
      <div className="flex gap-[3px] min-w-max">
        <div className="flex flex-col gap-[3px] mr-1 text-[9px] text-muted-foreground">
          <span className="h-3 leading-3" />
          {WEEKDAYS.map((w, i) => <span key={w} className="h-3 leading-3">{i % 2 === 0 ? w : ""}</span>)}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            <div className="h-3 text-[9px] text-muted-foreground leading-3">{weekLabels[wi]}</div>
            {week.map((d, di) => {
              const rec = d ? map[d]?.recovery : null;
              return (
                <div key={di}
                  onClick={() => d && map[d] && onDayClick(d)}
                  title={d ? `${d} · восстановление: ${rec != null ? Math.round(rec) + "%" : "нет данных"} (${zoneLabel(rec)})` : ""}
                  className={`w-3 h-3 rounded-sm ${d && map[d] ? "cursor-pointer hover:ring-1 hover:ring-white/60" : ""}`}
                  style={{ background: d ? recColor(rec) : "transparent", opacity: rec == null ? 0.5 : 0.4 + Math.min((rec ?? 0) / 100, 1) * 0.6 }}
                  data-testid={d && map[d] ? `heatmap-cell-${d}` : undefined}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#00e676]" /> 67–100%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#ffea00]" /> 34–66%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#ff3b30]" /> 0–33%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "hsl(220 10% 16%)" }} /> нет данных</span>
      </div>
    </div>
  );
}

export default function PatternsTab({ member = "" }) {
  const [all, setAll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayDate, setDayDate] = useState(null);

  useEffect(() => {
    api.get(`/whoop/analytics?days=0${member ? `&member=${member}` : ""}`)
      .then((r) => setAll(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground p-6" data-testid="patterns-loading">Загружаем полную историю (первый раз — до 30 секунд)…</p>;
  if (!all) return <p className="text-sm text-muted-foreground p-6">Не удалось загрузить историю</p>;

  const days = all.days;
  const streaks = computeStreaks(days);
  const wd = weekdayStats(days);
  const withRec = wd.filter((w) => w.recovery != null);
  const bestDay = withRec.length ? withRec.reduce((a, b) => (b.recovery > a.recovery ? b : a)) : null;
  const worstDay = withRec.length ? withRec.reduce((a, b) => (b.recovery < a.recovery ? b : a)) : null;
  const bestSleep = wd.filter((w) => w.sleep_h != null).reduce((a, b) => (b.sleep_h > (a?.sleep_h ?? -1) ? b : a), null);

  const statCards = [
    [<Fire size={18} weight="fill" className="text-[#00e676]" />, "Текущая серия зелёных дней", streaks.current, "streak-current"],
    [<Trophy size={18} weight="fill" className="text-[#ffea00]" />, "Рекордная серия", streaks.record, "streak-record"],
    [<Percent size={18} weight="fill" className="text-primary" />, "Зелёных дней за всё время", `${streaks.pct}%`, "streak-pct"],
    [<CalendarCheck size={18} weight="fill" className="text-[#a970ff]" />, "Дней с данными", streaks.withData, "streak-total"],
  ];

  return (
    <div className="space-y-4" data-testid="patterns-tab">
      <div className="flex justify-end">
        <MonthReportCard days={days} workouts={all.workouts || []} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(([icon, label, value, tid]) => (
          <div key={tid} className="glass-card p-5" data-testid={tid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{icon}{label}</div>
            <div className="font-heading text-3xl font-bold num">{value}</div>
          </div>
        ))}
      </div>

      <div className="glass-card p-5">
        <h3 className="text-sm font-semibold mb-1">Тепловая карта восстановления · последние 12 месяцев</h3>
        <p className="text-xs text-muted-foreground mb-4">Каждая клетка — день. Ярче зелёный — выше восстановление. Клик — детали дня.</p>
        <Heatmap days={days} onDayClick={setDayDate} />
      </div>

      <FitnessTrendCard trend={all.fitness_trend} />

      <YearCompareCard compare={all.year_compare} />

      <BestDaysCard bestDays={all.best_days} />

      <div className="glass-card p-5" data-testid="weekday-patterns">
        <h3 className="text-sm font-semibold mb-1">Паттерны по дням недели</h3>
        {bestDay && worstDay && (
          <p className="text-xs text-muted-foreground mb-4">
            Лучший день — <span className="text-[#00e676] font-semibold">{bestDay.day}</span> (восст. {bestDay.recovery}%),
            худший — <span className="text-[#ff3b30] font-semibold">{worstDay.day}</span> ({worstDay.recovery}%).
            {bestSleep && <> Дольше всего вы спите в <span className="text-[#a970ff] font-semibold">{bestSleep.day}</span> ({bestSleep.sleep_h} ч).</>}
          </p>
        )}
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={wd}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 16%)" vertical={false} />
            <XAxis dataKey="day" stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis yAxisId="rec" domain={[0, 100]} stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} width={30} />
            <YAxis yAxisId="h" orientation="right" stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} width={30} />
            <Tooltip contentStyle={{ background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="rec" dataKey="recovery" name="Восстановление %" fill="#00e676" radius={[3, 3, 0, 0]}>
              {wd.map((e, i) => <Cell key={i} fill={recColor(e.recovery)} fillOpacity={0.8} />)}
            </Bar>
            <Line yAxisId="h" type="monotone" dataKey="sleep_h" name="Сон, ч" stroke="#a970ff" strokeWidth={2} dot />
            <Line yAxisId="h" type="monotone" dataKey="strain" name="Нагрузка" stroke="#0084ff" strokeWidth={2} dot />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <DayDetailSheet date={dayDate} days={days} workouts={all.workouts || []} onClose={() => setDayDate(null)} />
    </div>
  );
}
